// Copyright (c) 2026, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { Fab, Fade } from "@mui/material";
import ReviewsIcon from "@mui/icons-material/Reviews";
import { LOCAL_STORAGE_KEYS, BANNER_TOGGLE_EVENT } from "../../config/constant";
import { AppStatus, BannerToggleDetail } from "../../utils/types";

interface BannerFabControllerProps {
  localStorageState: {
    welcomeHidden: boolean;
    notificationHidden: boolean;
  };
  contactState: {
    state: "failed" | "success" | "loading" | "idle";
  };
  appMaintenanceStatus: AppStatus;
}

const BannerFabController = ({
  localStorageState,
  contactState,
  appMaintenanceStatus,
}: BannerFabControllerProps) => {
  const bannerId = LOCAL_STORAGE_KEYS.NOTIFICATION_BANNER_DISMISSED;
  const welcomeKey = LOCAL_STORAGE_KEYS.WELCOME_DIALOG_DISMISSED;

  const welcomePresent = contactState.state === "success";
  const notificationPresent =
    appMaintenanceStatus.isShowMaintenanceBanner &&
    appMaintenanceStatus.maintenanceBannerContent;

  const welcomeIsOpen = !localStorageState.welcomeHidden;
  const notificationIsOpen = !localStorageState.notificationHidden;

  // If both present and both visible -> no FABs
  if (welcomePresent && notificationPresent && welcomeIsOpen && notificationIsOpen) {
    return null;
  }

  // Show combined FAB only when both banners are present AND both are closed
  // This ensures we don't show a combined FAB when only one banner exists
  const shouldShowCombinedFAB =
    welcomePresent && notificationPresent && !welcomeIsOpen && !notificationIsOpen;
  if (shouldShowCombinedFAB) {
    return (
      <Fade in timeout={300}>
        <Fab
          sx={{ position: "fixed", bottom: 12, right: 20, zIndex: 1000 }}
          color="secondary"
          aria-label="open banners"
          title="Open notifications and welcome"
          onClick={() => {
            localStorage.setItem(bannerId, "show");
            localStorage.setItem(welcomeKey, "show");
            window.dispatchEvent(
              new CustomEvent<BannerToggleDetail>(BANNER_TOGGLE_EVENT, { detail: { notification: true, welcome: true } })
            );
          }}
        >
          <ReviewsIcon />
        </Fab>
      </Fade>
    );
  }

  // If only notification is closed -> show notification FAB at bottom-right
  if (notificationPresent && !notificationIsOpen) {
    return (
      <Fade in timeout={300}>
        <Fab
          sx={{ position: "fixed", bottom: 12, right: 20, zIndex: 1000 }}
          color="secondary"
          aria-label="open notification banner"
          title="Open notification"
          onClick={() => {
            localStorage.setItem(bannerId, "show");
            window.dispatchEvent(new CustomEvent<BannerToggleDetail>(BANNER_TOGGLE_EVENT, { detail: { notification: true } }));
          }}
        >
          <ReviewsIcon />
        </Fab>
      </Fade>
    );
  }

  // If only welcome is closed -> show welcome FAB above welcome card
  if (welcomePresent && !welcomeIsOpen) {
    return (
      <Fade in timeout={300}>
        <Fab
          sx={{ position: "fixed", bottom: 12, right: 20, zIndex: 1000 }}
          color="secondary"
          aria-label="open welcome card"
          title="Open welcome"
          onClick={() => {
            localStorage.setItem(welcomeKey, "show");
            window.dispatchEvent(new CustomEvent<BannerToggleDetail>(BANNER_TOGGLE_EVENT, { detail: { welcome: true } }));
          }}
        >
          <ReviewsIcon />
        </Fab>
      </Fade>
    );
  }

  return null;
};

export default BannerFabController;
